import { Component, EventEmitter, effect, input, Output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MicVAD } from '@ricky0123/vad-web';
import { ButtonModule } from 'primeng/button';
import { MicStatusConfig, AudioRecording, MicStatusLiteral } from '../../models/mic.models';

@Component({
  selector: 'app-mic-vad',
  templateUrl: './mic-vad.component.html',
  styleUrls: ['./mic-vad.component.scss'],
  standalone: true,
  imports: [CommonModule, ButtonModule],
})
export class MicVadComponent {
  // Inputs
  defaultButtonLabel = 'Record'; // Renamed to avoid conflict with computed signal
  recordingLabel = 'Recording...';
  listeningLabel = 'Listening...';
  // Default icon and class values are now handled by computed signals
  buttonClass = 'p-button-rounded';

  continueListening = input<boolean>(false);
  // Outputs
  @Output() statusChanged = new EventEmitter<{ status: MicStatusLiteral; description: string }>();
  @Output() audioRecorded = new EventEmitter<AudioRecording>();
  @Output() error = new EventEmitter<string>();

  // State using signals for reactivity

  public status = signal<MicStatusLiteral>('ready');
  public isSpeechActive = signal<boolean>(false);

  public statusDescription = signal<string>(MicStatusConfig.ready.description);
  public currentStatusConfig = signal<MicStatusConfig>(MicStatusConfig.ready);

  // VAD instance
  private vadInstance: MicVAD | null = null;

  constructor() {
    // Effect to watch for changes to continueListening input
    effect(() => {
      const shouldContinue = this.continueListening();
      // If we're paused and parent signals to continue listening
      if (shouldContinue && this.vadInstance) {
        this.resumeListening();
      }
    });
  }

  /**
   * Toggle recording state
   */
  public toggleRecording(): void {
    const status = this.status();
    if (status === 'listening' || status === 'recording') {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

  /**
   * Computed signal for button icon based on current status
   */
  public buttonIcon = computed(() => this.currentStatusConfig().icon);

  /**
   * Computed signal for button label based on current status
   */
  public buttonLabel = computed(() => this.currentStatusConfig().buttonLabel);

  /**
   * Computed signal for button CSS classes based on current status
   */
  public buttonClasses = computed(() => {
    const statusConfig = this.currentStatusConfig();
    const currentStatus = this.status();

    return {
      [`p-button-${statusConfig.severity}`]: true,
      'speech-active': this.isSpeechActive(),
      'listening-active': currentStatus === 'listening',
    };
  });

  /**
   * Start the recording process
   */
  private async startRecording(): Promise<void> {
    try {
      this.updateStatus('initializing');
      this.statusDescription.set('Initializing...');

      // Create VAD instance with options
      const options = {
        onSpeechStart: () => {
          console.log('Speech detected');
          this.isSpeechActive.set(true);
          this.updateStatus('recording');
        },
        onSpeechEnd: async (audio: Float32Array) => {
          console.log('Speech ended');
          this.isSpeechActive.set(false);

          try {
            // Convert Float32Array to WAV format and create a blob
            const wavBlob = await this.float32ArrayToWav(audio, 16000);

            // Create a URL for the blob
            const audioUrl = URL.createObjectURL(wavBlob);

            // Create recording object
            const recording: AudioRecording = {
              id: Date.now().toString(),
              timestamp: new Date(),
              url: audioUrl,
              duration: Math.round(audio.length / 16000), // Approximate duration in seconds
              blob: wavBlob,
            };

            // Emit the recording
            this.audioRecorded.emit(recording);

            // Pause listening after emitting the recording
            if (this.vadInstance) {
              this.vadInstance.pause();
              this.updateStatus('inactive');
            }
          } catch (error) {
            console.error('Error processing audio:', error);
            this.updateStatus('inactive');
            this.statusDescription.set('Error processing audio');
            this.error.emit('Error processing audio');
          }
        },
      };

      this.vadInstance = await MicVAD.new(options);

      if (!this.vadInstance) {
        throw new Error('Failed to create VAD instance');
      }

      // Start the VAD
      this.vadInstance.start();
      this.updateStatus('listening');
    } catch (error) {
      console.error('Error starting VAD:', error);
      this.updateStatus('inactive');
      this.statusDescription.set(`Error starting recording: ${error}`);
      this.error.emit(`Error starting recording: ${error}`);
    }
  }

  /**
   * Stop the recording process
   */
  public stopRecording(): void {
    if (this.vadInstance) {
      this.vadInstance.pause();
      this.vadInstance.destroy();
      this.vadInstance = null;
      this.isSpeechActive.set(false);
      this.updateStatus('inactive');
    }
  }

  /**
   * Resume listening after being paused
   */
  private resumeListening(): void {
    if (this.vadInstance) {
      this.vadInstance.start();
      this.updateStatus('listening');

      // Reset the continueListening input to false to prepare for the next cycle
      // This is important to avoid triggering the effect again until parent explicitly sets it to true
      // Note: Since continueListening is an input, we can't directly modify it
      // The parent component needs to reset it after receiving the status change event
      this.statusChanged.emit({
        status: 'listening',
        description: MicStatusConfig.listening.description,
      });
    }
  }

  /**
   * Update the status and related UI state
   */
  private updateStatus(status: MicStatusLiteral): void {
    console.log('Updating status to:', status);
    this.status.set(status);
    const statusConfig = MicStatusConfig[status];
    this.currentStatusConfig.set(statusConfig);
    this.statusDescription.set(statusConfig.description);

    // Emit the status change event
    this.statusChanged.emit({
      status: status,
      description: statusConfig.description,
    });
  }

  /**
   * Convert Float32Array to WAV format
   */
  private float32ArrayToWav(float32Array: Float32Array, sampleRate: number): Promise<Blob> {
    return new Promise((resolve) => {
      // Create a buffer with space for the data
      const buffer = new ArrayBuffer(44 + float32Array.length * 2);
      const view = new DataView(buffer);

      // RIFF chunk descriptor
      this.writeString(view, 0, 'RIFF');
      view.setUint32(4, 36 + float32Array.length * 2, true);
      this.writeString(view, 8, 'WAVE');

      // FMT sub-chunk
      this.writeString(view, 12, 'fmt ');
      view.setUint32(16, 16, true); // subchunk size
      view.setUint16(20, 1, true); // PCM format
      view.setUint16(22, 1, true); // mono channel
      view.setUint32(24, sampleRate, true); // sample rate
      view.setUint32(28, sampleRate * 2, true); // byte rate
      view.setUint16(32, 2, true); // block align
      view.setUint16(34, 16, true); // bits per sample

      // Data sub-chunk
      this.writeString(view, 36, 'data');
      view.setUint32(40, float32Array.length * 2, true);

      // Write the PCM samples
      const length = float32Array.length;
      const index = 44;

      for (let i = 0; i < length; i++) {
        const s = Math.max(-1, Math.min(1, float32Array[i]));
        view.setInt16(index + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      }

      // Create the Blob
      const blob = new Blob([buffer], { type: 'audio/wav' });
      resolve(blob);
    });
  }

  /**
   * Helper method to write a string to a DataView
   */
  private writeString(view: DataView, offset: number, string: string): void {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }
}
